import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../../api/hooks/useSmartAndRegularAssets";
import { entityKeys } from "../../../../../../api/queryKeys";
import ImpactChip from "../../ControlRemediationDrawer/control/ImpactChip";
import ControlEntitiesActionButtons from "../shared/ControlEntitiesActionButtons";
import { applySearchFilter } from "../shared/controlEntitiesUtils";
import SearchTextField from "../shared/SearchTextField";
import EntityTypeAndStatusTabbedTable from "../tables/EntityTypeAndStatusTabbedTable";
import { MisconfigStatusTab } from "../tables/MisconfigStatusTabs";
import getMisconfiguredEntitiesTableColumns from "./misconfiguredEntitiesTableColumns";

const groupByStatus = (misconfigurations, entitiesByType) => {
  const entitiesByTypeAndExclusion = Object.keys(entitiesByType).reduce(
    (acc, entityType) => {
      const entities = entitiesByType[entityType];
      acc[entityType] = {
        [MisconfigStatusTab.INCLUDED]: [],
        [MisconfigStatusTab.EXCLUDED]: [],
        [MisconfigStatusTab.SNOOZED]: [],
      };

      const excludedMisconfigs = misconfigurations?.filter(
        (misconfiguration) => {
          return misconfiguration.is_excluded;
        }
      );
      const excludedMisconfigEntityIds = excludedMisconfigs?.map(
        (misconfiguration) => misconfiguration.entity_id
      );

      const snoozedMisconfigs = misconfigurations?.filter(
        (misconfiguration) => {
          return misconfiguration.is_snoozed;
        }
      );
      const snoozedMisconfigEntityIds = snoozedMisconfigs?.map(
        (misconfiguration) => misconfiguration.entity_id
      );

      entities.forEach((entity) => {
        const isExcluded = excludedMisconfigEntityIds.includes(
          entity.entity_id
        );
        const isSnoozed = snoozedMisconfigEntityIds.includes(entity.entity_id);
        acc[entityType][
          isExcluded
            ? MisconfigStatusTab.EXCLUDED
            : isSnoozed
            ? MisconfigStatusTab.SNOOZED
            : MisconfigStatusTab.INCLUDED
        ].push(entity);
      });
      return acc;
    },
    {}
  );
  return entitiesByTypeAndExclusion;
};

const prepareNonGroupedEntitiesByType = (
  misconfigurations,
  allEntitiesByType,
  searchText = "",
  appliedFilters = {},
  assets = []
) => {
  /* 
    Result example:
    {
      "host": {
        [MisconfigStatusTab.INCLUDED]: [{}, {}, ...],
        [MisconfigStatusTab.EXCLUDED]: [{}, {}, ...]
        [MisconfigStatusTab.SNOOZED]: [{}, {}, ...]
      },
      ...  
    }
  */
  const searchedEntitiesByType = applySearchFilter(
    allEntitiesByType,
    searchText,
    appliedFilters,
    assets
  );
  const entitiesByTypeAndStatus = groupByStatus(
    misconfigurations,
    searchedEntitiesByType
  );
  return entitiesByTypeAndStatus;
};

export default function MisconfiguredEntitiesSection({
  fullControl,
  allEntitiesByType,
  selectedEntityTypeTab,
  setSelectedEntityTypeTab,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [searchText, setSearchText] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    labels: {},
    assets: {},
  });
  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsAreLoading } =
    useSmartAndRegularAssets(false);

  const entitiesByTypeAndStatus = prepareNonGroupedEntitiesByType(
    fullControl?.misconfigurations,
    allEntitiesByType,
    searchText,
    appliedFilters,
    assets
  );

  const [selectedStatusTab, setSelectedStatusTab] = useState(
    MisconfigStatusTab.INCLUDED
  );
  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);

  const queryClient = useQueryClient();
  const terminatedEntities = queryClient.getQueryData(
    entityKeys.terminatedEntities()
  ).data;

  const columns = getMisconfiguredEntitiesTableColumns(
    fullControl,
    selectedEntityTypeTab,
    colors,
    terminatedEntities,
    selectedStatusTab
  );

  const handleCheckedChange = (newCheckedEntityIDs) => {
    trackEvent("User checked a control misconfigured entity");
    setCheckedEntityIDs(newCheckedEntityIDs);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box
        display="flex"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <ImpactChip
          numberOfEntities={
            entitiesByTypeAndStatus[selectedEntityTypeTab][selectedStatusTab]
              ?.length || 0
          }
        />
        <Box display="flex" alignItems="center" gap={1}>
          <SearchTextField
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <ControlEntitiesActionButtons
            fullControl={fullControl}
            entitiesByTypeAndStatus={entitiesByTypeAndStatus}
            checkedEntityIDs={checkedEntityIDs}
            selectedEntityTypeTab={selectedEntityTypeTab}
            selectedStatusTab={selectedStatusTab}
            actionsOnMisconfigurations={true}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
      </Box>
      <EntityTypeAndStatusTabbedTable
        entitiesByTypeAndStatus={entitiesByTypeAndStatus}
        columns={columns}
        selectedEntityTypeTab={selectedEntityTypeTab}
        setSelectedEntityTypeTab={setSelectedEntityTypeTab}
        selectedStatusTab={selectedStatusTab}
        setSelectedStatusTab={setSelectedStatusTab}
        handleCheckedChange={handleCheckedChange}
      />
    </Box>
  );
}
